import { useCallback, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";
import type { CountryCode } from "@wayflyer/flyui";
import { IconMapPin16Line } from "@wayflyer/flyui-icons/16/line";

import { usePlacesAutocomplete } from "../../hooks/use-places-autocomplete/use-places-autocomplete";
import type { ParsedAddress, PlaceDetails, FormMethods } from "../../hooks/use-places-autocomplete/google-places";

export type EditableAddressFormValues = {
  country_code: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip_code: string;
};

const formatAddress = (values: EditableAddressFormValues): string =>
  [values.address_line_1, values.address_line_2, values.city, values.state, values.zip_code].filter(Boolean).join(", ");

interface FieldMapping {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country_code: string;
}

interface useEditableAddressSearchProps {
  form: UseFormReturn<any>;
  handleAddressSelect: (address: ParsedAddress, details: PlaceDetails, mainText?: string) => void;
  fieldName?: string;
  countries?: CountryCode[];
  states?: string[];
  onManualEntry?: () => void;
  fieldMapping?: FieldMapping;
  onAddressChange?: (optionId: string | number | null) => void;
  parentFormFieldName?: string;
}

export const useEditableAddressSearch = ({
  form,
  handleAddressSelect,
  fieldName = "address_search",
  countries,
  states,
  onManualEntry,
  fieldMapping,
  onAddressChange,
  parentFormFieldName,
}: useEditableAddressSearchProps) => {
  const [isManualEntryModalOpen, setIsManualEntryModalOpen] = useState(false);
  const [manualEntryAddress, setManualEntryAddress] = useState<EditableAddressFormValues | null>(null);

  const formAdapter: FormMethods<any> = useMemo(
    () => ({
      setValue: (name: string | number | symbol, value: string) => {
        form.setValue(name as any, value, { shouldDirty: true });
      },
      getValues: (name?: string | number | symbol) => {
        if (name) {
          return form.getValues(name as any) || "";
        }
        return "";
      },
      clearErrors: (name?: string | number | symbol) => {
        if (name) {
          form.clearErrors(name as any);
        }
      },
    }),
    [form]
  );

  const addressAutocomplete = usePlacesAutocomplete({
    countries,
    form: formAdapter,
    states,
    fieldName,
    onAddressSelect: (address, details, mainText) => {
      handleAddressSelect(address, details, mainText);
    },
  });

  const formFieldValue = useWatch({ control: form.control, name: fieldName });

  const transformedOptions = useMemo(() => {
    const mappedOptions = addressAutocomplete.options.map((option) => {
      const isSelected = formFieldValue && typeof formFieldValue === "string" && option.id === formFieldValue;

      const displayText =
        isSelected && addressAutocomplete.selectedDisplayText
          ? addressAutocomplete.selectedDisplayText
          : option.prediction.description.text || option.prediction.structured_formatting.main_text.text;

      return {
        id: option.id,
        text: displayText,
        subText: option.prediction.structured_formatting.secondary_text.text,
        icon: <IconMapPin16Line />,
      };
    });

    if (
      formFieldValue &&
      typeof formFieldValue === "string" &&
      addressAutocomplete.selectedDisplayText &&
      !mappedOptions.some((opt) => opt.id === formFieldValue)
    ) {
      return [
        {
          id: formFieldValue,
          text: addressAutocomplete.selectedDisplayText,
          subText:
            addressAutocomplete.selectedAddress?.city && addressAutocomplete.selectedAddress?.state
              ? `${addressAutocomplete.selectedAddress.city}, ${addressAutocomplete.selectedAddress.state}`
              : "",
          icon: <IconMapPin16Line />,
        },
        ...mappedOptions,
      ];
    }

    return mappedOptions;
  }, [
    addressAutocomplete.options,
    addressAutocomplete.selectedDisplayText,
    addressAutocomplete.selectedAddress,
    formFieldValue,
  ]);

  const handleManualEntry = useCallback(
    (isExplicit = false) => {
      if (!isExplicit) {
        return;
      }

      if (fieldMapping) {
        const address = {
          country_code: form.getValues(fieldMapping.country_code) || "US",
          address_line_1: form.getValues(fieldMapping.address_line_1) || "",
          address_line_2: form.getValues(fieldMapping.address_line_2) || "",
          city: form.getValues(fieldMapping.city) || "",
          state: form.getValues(fieldMapping.state) || "",
          zip_code: form.getValues(fieldMapping.zip_code) || "",
        };
        setManualEntryAddress(address);
      } else {
        const addressForm = form as unknown as UseFormReturn<EditableAddressFormValues>;
        setManualEntryAddress({
          country_code: addressForm.getValues("country_code") || "US",
          address_line_1: addressForm.getValues("address_line_1") || "",
          address_line_2: addressForm.getValues("address_line_2") || "",
          city: addressForm.getValues("city") || "",
          state: addressForm.getValues("state") || "",
          zip_code: addressForm.getValues("zip_code") || "",
        });
      }
      form.clearErrors(fieldName);
      setIsManualEntryModalOpen(true);
    },
    [form, fieldName, fieldMapping]
  );

  const handleModalSave = useCallback(
    (addressValues: EditableAddressFormValues) => {
      if (fieldMapping) {
        form.setValue(fieldMapping.country_code, addressValues.country_code, { shouldDirty: true });
        form.setValue(fieldMapping.address_line_1, addressValues.address_line_1, { shouldDirty: true });
        form.setValue(fieldMapping.address_line_2, addressValues.address_line_2 || "", { shouldDirty: true });
        form.setValue(fieldMapping.city, addressValues.city, { shouldDirty: true });
        form.setValue(fieldMapping.state, addressValues.state, { shouldDirty: true });
        form.setValue(fieldMapping.zip_code, addressValues.zip_code, { shouldDirty: true });
      } else {
        const addressForm = form as unknown as UseFormReturn<EditableAddressFormValues>;
        addressForm.setValue("country_code", addressValues.country_code);
        addressForm.setValue("address_line_1", addressValues.address_line_1);
        addressForm.setValue("address_line_2", addressValues.address_line_2 || "");
        addressForm.setValue("city", addressValues.city);
        addressForm.setValue("state", addressValues.state);
        addressForm.setValue("zip_code", addressValues.zip_code);
      }

      const formattedAddress = formatAddress(addressValues);

      form.setValue(fieldName, formattedAddress as any, {
        shouldDirty: true,
      });
      form.clearErrors(fieldName);

      // Update addressAutocomplete internal state with manually entered address
      const manualParsedAddress: ParsedAddress = {
        streetNumber: undefined,
        streetName: addressValues.address_line_1,
        city: addressValues.city,
        state: addressValues.state,
        country: addressValues.country_code,
        postalCode: addressValues.zip_code,
        formattedAddress: formattedAddress,
      };

      addressAutocomplete.setManualAddress(manualParsedAddress, formattedAddress);

      if (parentFormFieldName) {
        form.trigger(parentFormFieldName);
      }

      setIsManualEntryModalOpen(false);
      setManualEntryAddress(null);
    },
    [form, fieldName, fieldMapping, parentFormFieldName, addressAutocomplete]
  );

  const handleModalCancel = useCallback(() => {
    setIsManualEntryModalOpen(false);
    setManualEntryAddress(null);
  }, []);

  const handleAddressChange = useCallback(
    (optionId: string | number | null) => {
      if (optionId === null) {
        if (fieldMapping) {
          const currentAddress1 = form.getValues(fieldMapping.address_line_1);
          const currentCity = form.getValues(fieldMapping.city);

          if (currentAddress1 || currentCity) {
            form.setValue(fieldMapping.address_line_1, "", { shouldDirty: true });
            form.setValue(fieldMapping.address_line_2, "", { shouldDirty: true });
            form.setValue(fieldMapping.city, "", { shouldDirty: true });
            form.setValue(fieldMapping.state, "", { shouldDirty: true });
            form.setValue(fieldMapping.zip_code, "", { shouldDirty: true });
            form.setValue(fieldMapping.country_code, "US", { shouldDirty: true });

            form.setValue(fieldName, "", { shouldDirty: true });
            onAddressChange?.(null);
          }
        } else {
          const formValues = form.getValues();
          if (Object.keys(formValues).length > 0) {
            form.reset();
            onAddressChange?.(null);
          }
        }
        return;
      }

      const selectedOption = addressAutocomplete.options.find((option) => option.id === optionId);
      if (!selectedOption) {
        return;
      }

      addressAutocomplete.onChange(selectedOption);
      onAddressChange?.(optionId);
    },
    [addressAutocomplete, form, fieldMapping, onAddressChange, fieldName]
  );

  const createableConfig = {
    content: "Enter manually",
    onAction: () => {
      form.resetField(fieldName);
      form.clearErrors(fieldName);
      onManualEntry?.();
    },
  };

  return {
    addressAutocomplete,
    transformedOptions,
    handleAddressChange,
    handleManualEntry,
    isManualEntryModalOpen,
    manualEntryAddress,
    handleModalSave,
    handleModalCancel,
    createableConfig,
  };
};
